import { Box, chakra, Flex, Image, Text, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import { route } from 'nextjs-routes';

import config from 'configs/app';
import useApiQuery from 'lib/api/useApiQuery';
import { useI18n } from 'lib/i18n/context';
import { HOMEPAGE_STATS } from 'stubs/stats';
import Skeleton from 'ui/shared/chakra/Skeleton';
import Hint from 'ui/shared/Hint';
import IconSvg from 'ui/shared/IconSvg';
import LinkInternal from 'ui/shared/links/LinkInternal';

import HomeStatsChart from './HomeStatsChart';
import useFetchChartData from './indicators/useFetchChartData';
import INDICATORS from './indicators/utils/indicators';

const DESKTOP_CARD_HEIGHT = '200px';
const DESKTOP_GAP = '24px';
const DESKTOP_PADDING = '16px';
const DESKTOP_DIVIDER_HEIGHT = '120px';
const DESKTOP_CHART_WIDTH = '280px';
const DESKTOP_CHART_HEIGHT = '113px';

type StatCellData = {
  iconSrc: string;
  label: string;
  value: React.ReactNode;
  href?: string;
};

type StatColumnProps = {
  first: StatCellData;
  second: StatCellData;
  isLoading: boolean;
  borderColor: string;
  mutedColor: string;
  valueColor: string;
};

const StatCell = ({
  iconSrc,
  label,
  value,
  href,
  isLoading,
  mutedColor,
  valueColor,
}: StatCellData & { isLoading: boolean; mutedColor: string; valueColor: string }) => {
  const content = (
    <Flex alignItems="center" gap="16px" minH="52px" cursor={ href ? 'pointer' : undefined }>
      <Image src={ iconSrc } alt="" boxSize="32px" flexShrink={ 0 }/>
      <Box>
        <Skeleton isLoaded={ !isLoading }>
          <Text
            color={ mutedColor }
            fontSize="14px"
            lineHeight="24px"
            fontWeight={ 500 }
            fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
          >
            { label }
          </Text>
        </Skeleton>
        <Skeleton isLoaded={ !isLoading } mt="8px">
          <Text
            color={ valueColor }
            fontSize="20px"
            lineHeight="24px"
            fontWeight={ 500 }
            fontFamily="'Oxanium', var(--chakra-fonts-body)"
          >
            { value }
          </Text>
        </Skeleton>
      </Box>
    </Flex>
  );

  if (href) {
    return (
      <LinkInternal href={ href } _hover={{ textDecoration: 'none' }}>
        { content }
      </LinkInternal>
    );
  }

  return content;
};

const ColumnDivider = ({ borderColor }: { borderColor: string }) => <Box h="1px" bg={ borderColor } w="100%"/>;

const VerticalDivider = ({ borderColor }: { borderColor: string }) => (
  <Box
    display={{ base: 'none', lg: 'block' }}
    w="1px"
    h={ DESKTOP_DIVIDER_HEIGHT }
    bg={ borderColor }
    alignSelf="center"
    flexShrink={ 0 }
  />
);

const StatColumn = ({ first, second, isLoading, borderColor, mutedColor, valueColor }: StatColumnProps) => {
  return (
    <Flex direction="column" justifyContent="center" gap="16px" minH={{ base: 'auto', lg: '168px' }}>
      <StatCell { ...first } isLoading={ isLoading } mutedColor={ mutedColor } valueColor={ valueColor }/>
      <ColumnDivider borderColor={ borderColor }/>
      <StatCell { ...second } isLoading={ isLoading } mutedColor={ mutedColor } valueColor={ valueColor }/>
    </Flex>
  );
};

const ChartHeader = ({
  title,
  hint,
  value,
  isLoading,
  mutedColor,
  valueColor,
  infoColor,
}: {
  title: string;
  hint?: React.ReactNode;
  value: React.ReactNode;
  isLoading: boolean;
  mutedColor: string;
  valueColor: string;
  infoColor: string;
}) => {
  return (
    <Flex justifyContent="space-between" alignItems="center">
      <Flex alignItems="center" gap="4px">
        <Text
          color={ mutedColor }
          fontSize="14px"
          lineHeight="24px"
          fontWeight={ 500 }
          fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
        >
          { title }
        </Text>
        { hint ? (
          <Hint
            label={ hint }
            tooltipProps={{
              hasArrow: true,
              bg: '#2F2F2F',
              color: '#FFFFFF',
              borderRadius: '8px',
              px: '16px',
              py: '12px',
              maxW: '560px',
            }}
          />
        ) : (
          <IconSvg name="info" boxSize="14px" color={ infoColor }/>
        ) }
      </Flex>
      <Skeleton isLoaded={ !isLoading }>
        <Text
          color={ valueColor }
          fontSize="14px"
          lineHeight="24px"
          fontWeight={ 500 }
          fontFamily="'Oxanium', var(--chakra-fonts-body)"
        >
          { value }
        </Text>
      </Skeleton>
    </Flex>
  );
};

const formatCompactValue = (value: number | string | null | undefined) => {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  return Number(value).toLocaleString();
};

const formatUsdValue = (value: string | null | undefined, maximumFractionDigits = 0) => {
  if (!value || Number(value) === 0) {
    return 'N/A';
  }

  return `$${ Number(value).toLocaleString(undefined, { maximumFractionDigits }) }`;
};

const PriceChange = ({ percentage }: { percentage: number | null }) => {
  if (percentage === null) {
    return null;
  }

  const isUp = percentage >= 0;

  return (
    <chakra.span
      color={ isUp ? '#14DCAC' : '#E74C3C' }
      fontSize="14px"
      lineHeight="24px"
      ml="8px"
    >
      ({ isUp ? '+' : '' }{ percentage.toFixed(2) }%)
    </chakra.span>
  );
};

const Stats = () => {
  const borderColor = useColorModeValue('#E9ECEF', '#222222');
  const cardBg = useColorModeValue('#FFFFFF', '#111111');
  const mutedColor = useColorModeValue('#616161', '#999999');
  const valueColor = useColorModeValue('#000000', '#FFFFFF');
  const infoColor = useColorModeValue('#999999', '#616161');
  const boxIconSrc = useColorModeValue('/static/homepage/stats-box-light.svg', '/static/homepage/stats-box.svg');
  const walletIconSrc = useColorModeValue('/static/homepage/stats-wallet-light.svg', '/static/homepage/stats-wallet.svg');
  const timerIconSrc = useColorModeValue('/static/homepage/stats-timer-light.svg', '/static/homepage/stats-timer.svg');
  const transactionsIconSrc = useColorModeValue('/static/homepage/stats-transactions-light.svg', '/static/homepage/stats-transactions.svg');
  const globeIconSrc = useColorModeValue('/static/homepage/stats-globe-light.svg', '/static/homepage/stats-globe.svg');
  const { data, isPlaceholderData, isError } = useApiQuery('stats', {
    queryOptions: {
      refetchOnMount: false,
      placeholderData: HOMEPAGE_STATS,
    },
  });
  const { t } = useI18n();

  const chartIndicator = INDICATORS.find(({ id }) => id === 'daily_txs');
  const chartQueryResult = useFetchChartData(chartIndicator);

  if (isError || !data) {
    return null;
  }

  const priceColumn: StatColumnProps = {
    first: {
      iconSrc: '/static/address/bot-balance-icon.svg',
      label: t('home.botPrice', { symbol: config.chain.currency.symbol }),
      value: (
        <>
          { formatUsdValue(data.coin_price, 6) }
          <PriceChange percentage={ data.coin_price_change_percentage }/>
        </>
      ),
    },
    second: {
      iconSrc: globeIconSrc,
      label: t('home.marketCap'),
      value: formatUsdValue(data.market_cap),
    },
    isLoading: isPlaceholderData,
    borderColor,
    mutedColor,
    valueColor,
  };

  const leftColumn: StatColumnProps = {
    first: {
      iconSrc: boxIconSrc,
      label: t('home.totalBlocks'),
      value: formatCompactValue(data.total_blocks),
      href: route({ pathname: '/blocks' }),
    },
    second: {
      iconSrc: walletIconSrc,
      label: t('home.walletAddresses'),
      value: formatCompactValue(data.total_addresses),
    },
    isLoading: isPlaceholderData,
    borderColor,
    mutedColor,
    valueColor,
  };

  const middleColumn: StatColumnProps = {
    first: {
      iconSrc: timerIconSrc,
      label: t('home.averageBlockTime'),
      value: `${ (data.average_block_time / 1000).toFixed(1) }s`,
    },
    second: {
      iconSrc: transactionsIconSrc,
      label: t('home.totalTransactions'),
      value: formatCompactValue(data.total_transactions),
      href: route({ pathname: '/txs' }),
    },
    isLoading: isPlaceholderData,
    borderColor,
    mutedColor,
    valueColor,
  };

  const chartValue = formatCompactValue(data.transactions_today);

  return (
    <Box border="1px solid" borderColor={ borderColor } borderRadius="8px" bg={ cardBg } overflow="hidden">
      <Flex
        display={{ base: 'none', lg: 'flex' }}
        alignItems="stretch"
        gap={ DESKTOP_GAP }
        px={ DESKTOP_PADDING }
        py={ DESKTOP_PADDING }
        h={ DESKTOP_CARD_HEIGHT }
      >
        <Box flex="1" minW={ 0 }>
          <StatColumn { ...priceColumn }/>
        </Box>
        <VerticalDivider borderColor={ borderColor }/>
        <Box flex="1" minW={ 0 }>
          <StatColumn { ...leftColumn }/>
        </Box>
        <VerticalDivider borderColor={ borderColor }/>
        <Box flex="1" minW={ 0 }>
          <StatColumn { ...middleColumn }/>
        </Box>
        <VerticalDivider borderColor={ borderColor }/>
        <Flex direction="column" w={ DESKTOP_CHART_WIDTH } flexShrink={ 0 } pt="6px">
          <ChartHeader
            title={ t('home.dailyTransactions') }
            hint={ t('home.numberOfTransactionsYesterday0002359UtcThe') }
            value={ chartValue }
            isLoading={ isPlaceholderData }
            mutedColor={ mutedColor }
            valueColor={ valueColor }
            infoColor={ infoColor }
          />
          <Box mt="26px" h={ DESKTOP_CHART_HEIGHT }>
            <HomeStatsChart { ...chartQueryResult }/>
          </Box>
        </Flex>
      </Flex>

      <Flex display={{ base: 'flex', lg: 'none' }} direction="column" px="16px" py="16px" gap="16px">
        <StatColumn { ...priceColumn }/>
        <ColumnDivider borderColor={ borderColor }/>
        <StatColumn { ...leftColumn }/>
        <ColumnDivider borderColor={ borderColor }/>
        <StatColumn { ...middleColumn }/>
        <ColumnDivider borderColor={ borderColor }/>
        <Flex direction="column" pt="6px">
          <ChartHeader
            title={ t('home.dailyTransactions') }
            hint={ t('home.numberOfTransactionsYesterday0002359UtcThe') }
            value={ chartValue }
            isLoading={ isPlaceholderData }
            mutedColor={ mutedColor }
            valueColor={ valueColor }
            infoColor={ infoColor }
          />
          <Box mt="26px" h={ DESKTOP_CHART_HEIGHT }>
            <HomeStatsChart { ...chartQueryResult }/>
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
};

export default Stats;
